import {RecordUtil} from '../infra/RecordUtil.service';
/**
 *
 * @ngdoc service
 * @name starter.service:searchService
 * @description
 * The access point to searching for results/clustering/recommendation
 * This service creates the request and parese the response in to the foam format which will be used to present the results
 *
 *
 * @example
 * searchService.search(this.query);
 * searchService.next_results();
 * searchService.previous_results();
 */

import {Cheetah} from './cheetah.service';
import {FacetService} from './facet/facet.service';
import {FullViewService} from './fullView/fullView.service';
import {SearchResultParser} from './searchResultParser.service';
import {SearchBarService} from './searchBar/searchBar.service';
import {SearchStateService, ISearchObject} from './searchState.service';
import {ConfigurationUtil} from '../nddUtils/configurationUtil.service';
import {SearchUtil} from '../nddUtils/searchUtil.service';
import {
    getBlendFacetsSeparatelyConfig,
    getLocationsConfiguration,
    getPrimoVeSkipDelivery,
    getPrimoVeDeliveryBulkFromBrief,
    getPrimoSplitFacetsMaxWait,
    getPrimoVeSplitFacetsWaitInterval,
    isGTISearch,
    getInstitutionCode,
    getLocalFieldsToDisplayFromAllDedupMembers,
    getNewspaperSearchFilterByAvailability
} from '../appConfig/appConfigForward';
import {IAlertMsg} from '../infra/widgets/alerts/alertMsg.interface';
import {AlertFactory, ALERT_MSG_TYPE} from '../infra/widgets/alerts/alertFactory.service';
import {JwtUtilService} from '../security/jwtUtil.service';
import {RtaService} from './rta.service';
import {SearchFieldsService} from './search.fields.service';
import {getVid, getCurrentPCKey, getTab, getSEOParamsMapping} from '../appConfig/appConfigForward';
import {Injectable, Inject} from 'ng-metadata/core';
import {SnippetService} from './snippet/snippet.service';
import {HighlightsService} from './highlight/highlight.service';
import {STATE_NAMES} from '../primoExplore/primoExploreStates.module';
import {RestUtils} from '../infra/restUtils.service';
import {PrimoResponse} from '../infra/dataStructures/PrimoResponses/searchResponseInterface';
import {preventStateReload} from '../infra/annotations';
import {UserSessionManagerService} from '../security/userSessionManager.service';
import {TimesCitedService} from './timesCited/timesCited.service';
import {updateZoteroPlugin} from '../nddUtils/zoteroUtil';
import {SearchHistoryService} from '../favorites/searchHistory/searchHistory.service';
import {FilterInstitution} from '../nddUtils/filterInstitution.service'
import {JournalsService} from "../journals/journals.service";
import {DatabasesService} from "../journals/databases.service";
import {ResourceRecommenderService} from "./resourceRecommender/resourceRecommender.service";
import {NewspapersService} from '../newspapers/newspapers.service';
import {SearchErrorMessageService} from './searchResult/errorMessage/searchErrorMessage.service';
import {TranslationService} from "../translate/translation.service";
import {QuickLink} from "./quickLink/quickLink.service";
import {PageNavMenuService} from './searchResult/searchResultList/pageNavMenu/pageNavMenu.service';
import {PagesTitlesService} from '../infra/accessibility/pagesTitles.service';
import {MappingTablesCache} from '../mappingTablesCache/mappingTablesCache.service';
import {PrimoResponse} from "../infra/dataStructures/PrimoResponses/doc.interfcae";
import {CitationTrailsExpandButtonService} from './citationTrails/citationTrailsExpandButton.service';
import {AnalyticsUtils} from "../nddUtils/analyticsUtils.service";
import {AuthoritySearchService} from './authority/authoritySearch.service';
import {
    HandleLimitOffsetErrorResult,
    RestrictedOffsetHandlerService
} from "../security/restrictedOffsetHandler.service";
import IDoc = PrimoResponse.IDoc;

const flatten = require('lodash/flatten');
const forEach = require('lodash/forEach');
const endsWith = require('lodash/endsWith');
const he = require('he');
const join = require('lodash/join');
const remove = require('lodash/remove');
const findIndex = require('lodash/findIndex');
const _get = require('lodash/get');
const _set = require('lodash/set');
const isArray = require('lodash/isArray');
const chunk = require('lodash/chunk');

@Injectable()
export class SearchService {
    private searchDone = false;
    private locationsTileConfiguration = getLocationsConfiguration();
    private _showMobileFacets = true;
    private _signInAlert: IAlertMsg;
    private _browseGapAlert: IAlertMsg;
    private _afterRedirectAlert: IAlertMsg;
    public activeFacets = undefined;

    private vid: string = getVid();

    public cameFromState: string;
    public cameFromParams:Object = {};
    public beaconResultsNumber;
    public needToRender: boolean = true;

    public isFacetExact: boolean; //to detect if 'facet exact' search option was made from lateral links

    /* private members */
    private query: any;

    private reloadSearch: boolean = false;
    private blendFacetsSeparatelyConf: boolean;
    private searchObjectBuffer: ISearchObject[] = [];

    private ENABLE_HIGHLIGHT_THE_RECORD_YOU_CAME_FROM = "enable_highlight_the_record_you_came_from";
    public YOUR_SELECTED_RECORD_ITEM = "yourSelectedRecordItem";
    public expandedSearchAfterZeroResults = false;
    public lateralLinkingRecordType: string;
    public lateralLinkingRecordTitle: string;
    public lateralLinkingRecordID: string;
    public deliveryPnxUpdatedFields = ['pnx.display.crsinfo', 'pnx.display.relation', 'pnx.display.series', 'pnx.display.ispartof', 'pnx.display.boundwith', 'pnx.display.dedupmemberids'];



    constructor(@Inject('$http') private $http: angular.IHttpService,
                private cheetah: Cheetah,
                @Inject('$stateParams') private $stateParams: angular.ui.IStateParamsService,
                @Inject('restBaseURLs') private restBaseURLs,
                private searchHistoryService: SearchHistoryService,
                private searchResultParser: SearchResultParser,
                private searchStateService: SearchStateService,
                private timesCitedService: TimesCitedService,
                private pageNavMenuService: PageNavMenuService,
                public facetService: FacetService,
                public fullViewService: FullViewService,
                @Inject('limit') private limit,
                @Inject('imagesBarLimit') private imagesBarLimit,
                private configurationUtil: ConfigurationUtil,
                private searchUtil: SearchUtil,
                @Inject('$state') private $state: ng.ui.IStateService,
                @Inject('$location') private $location: ng.ILocationService,
                private searchBar: SearchBarService,
                private searchFieldsService: SearchFieldsService,
                private searchErrorMessageService: SearchErrorMessageService,
                private jwtUtilService: JwtUtilService,
                private alertService: AlertFactory,
                private rtaService: RtaService,
                private databasesService: DatabasesService,
                private resourceRecommender: ResourceRecommenderService,
                private snippetService: SnippetService,
                public highlightsService: HighlightsService,
                private restUtils: RestUtils,
                @Inject('$httpParamSerializer') private $httpParamSerializer: ng.IHttpParamSerializer,
                private userSessionManagerService: UserSessionManagerService,
                private recordUtil: RecordUtil,
                private filterInstitution: FilterInstitution,
                @Inject('$translate') private $translate: ng.translate.ITranslateService,
                private journalsService: JournalsService,
                @Inject('$rootScope') private $rootScope: ng.IRootScopeService,
                private newspapersService: NewspapersService,
                @Inject('$q') private $q: ng.IQService,
                private quickLinkService: QuickLink,
                @Inject('$timeout') private $timeout: ng.ITimeoutService,
                private translationService: TranslationService,
                private mappingTablesCache : MappingTablesCache,
                private pagesTitlesService: PagesTitlesService,
                private citationTrailsExpandButtonService: CitationTrailsExpandButtonService,
                private analyticsUtils: AnalyticsUtils,
                private authoritySearchService: AuthoritySearchService,
                private restrictedOffsetHandlerService: RestrictedOffsetHandlerService) {
        this.limit = limit;
        this.imagesBarLimit = imagesBarLimit;
        this.query = '';
        this.cameFromState = '';
        this.isFacetExact = false;
        //this.init();
        this._signInAlert = this.alertService.create(ALERT_MSG_TYPE.SIGNIN);
        this._browseGapAlert = this.alertService.create(ALERT_MSG_TYPE.BROWSE_GAP);
        this._afterRedirectAlert = this.alertService.create(ALERT_MSG_TYPE.SIMPLE);
        this.showAfterRedirectAlert();

        this.blendFacetsSeparatelyConf = getBlendFacetsSeparatelyConfig();
        let localFieldsToDisplayFromAllDedupMembers = getLocalFieldsToDisplayFromAllDedupMembers();
        if(localFieldsToDisplayFromAllDedupMembers && localFieldsToDisplayFromAllDedupMembers.length > 0) {
            localFieldsToDisplayFromAllDedupMembers.forEach((key) => {
                this.deliveryPnxUpdatedFields.push("pnx.display." + key.target)
            });
        }
    }

    get signInAlert(): IAlertMsg {
        return this._signInAlert;
    }

    get browseGapAlert(): IAlertMsg {
        return this._browseGapAlert;
    }

    get afterRedirectAlert(): IAlertMsg {
        return this._afterRedirectAlert;
    }

    public sendAnalytics(searchType) {
        let payload = {
            'Search Type': searchType,
            'Search Term': this.getSearchTermsFromUrl(),
            'Results Number': _get(this.getResultObject(),'info.total'),
            'Scope Type': this.capitalizeFirstLetter(this.getSourceOfTheRecord()),
            'Scope Name': this.getSearchTab(),
            'Scope Label': this.getScopeLabel(this.getSearchTab()),
            'Index Type': this.capitalizeFirstLetter(this.getFieldSearchedFromUrl()),
            'Index Type Name': this.getIndexTypeLabel(this.getFieldSearchedFromUrl()),
            'Operator Type': this.getOperatorType(this.getSearchTypeFromUrl()),
            'Facet Used': this.getFacetUsed(),
            'Expand Category': '',
            'Browse Category': ''
        }

        let mode = this.$stateParams['mode'];
        if (mode == 'advanced') {
            searchType = 'Advanced';
        }
        switch (searchType) {
            case 'Basic':
                if (angular.isArray(payload['Index Type'])) {
                    payload['Index Type'] = payload['Index Type'][0];
                }
                if (angular.isArray(payload['Operator Type'])) {
                    payload['Operator Type'] = payload['Operator Type'][0];
                }
                if (angular.isArray(payload['Search Term'])) {
                    payload['Search Term'] = payload['Search Term'][0];
                    let splitted = payload['Search Term'].split(',');
                    let conditions = ['AND','OR'];
                    if (conditions.includes(splitted[splitted.length-1])) {
                        splitted.splice(splitted.length-1,1);
                        payload['Search Term'] = splitted.join(',');
                    }
                }
                break;
            case 'Advanced':
                payload['Resource Type'] = this.getResourceTypePreFilterFromUrl();

                this.setArrayParamterAsString(payload,'Search Term');
                this.setArrayParamterAsString(payload,'Index Type');
                this.setArrayParamterAsString(payload,'Operator Type');
                payload['Advanced Query String'] = this.getQueryTerms();
                break;
            case 'Filter Bar':
                payload['Resource Type'] = this.getResourceTypePreFilterFromUrl();
                this.setArrayParamterAsString(payload,'Search Term');
                this.setArrayParamterAsString(payload,'Index Type');
                this.setArrayParamterAsString(payload,'Operator Type');
                break;
        }

        this.analyticsUtils.trackUsage(this.analyticsUtils.eventsNames.SEARCH, payload);
    }

    public capitalizeFirstLetter(str: string){
        if(str == '')
            return '';
        if(isArray(str)){
            str = str[0];
        }
        return str.split(/\s+/)
            .map(word => word.length > 0? word.charAt(0).toUpperCase() + word.slice(1) : '')
            .join('')
    }

    private setArrayParamterAsString(payload, parameter) {
        if (!payload[parameter]) {
            return;
        }
        if (angular.isArray(payload[parameter])) {
            if (payload[parameter].length > 1) {
                delete payload[parameter];
            } else {
                payload[parameter] = payload[parameter][0];
            }
        }
    }

    private getResourceTypePreFilterFromUrl() {
        let resourceType = 'All items'
        let preFilter = this.$stateParams['pfilter'];
        if (preFilter) {
            preFilter = flatten([preFilter]);
            preFilter.forEach((filter, index, array) => {
                let arr = filter.split(',');
                if (arr.length >= 3) {
                    resourceType = arr[2];
                }
            });
        }
        return resourceType;
    }

    private getFacetUsed() {
        //facet=topic,include,Marine,lk&facet=rtype,include,books&
        let facets = this.$stateParams['facet'];
        let mFacets = this.$stateParams['mfacet'];
        let allFacets = [];
        let facetUsed = 'None';

        if (facets && facets.length > 0 && facets[0]) {
            if (!angular.isArray(facets)) {
                facets = [facets];
            }
            allFacets = allFacets.concat(facets);
        }

        if (mFacets && mFacets.length > 0 && mFacets[0]) {
            if (!angular.isArray(mFacets)) {
                mFacets = [mFacets];
            }
            allFacets = allFacets.concat(mFacets);
        }




        if (!allFacets || allFacets.length == 0) {
            return facetUsed;
        }
        for (let i=0;i<allFacets.length;i++) {
            let cur = allFacets[i];
            let splitted = cur.split(',');
            if (splitted[splitted.length - 1] == 'lk') {
                facetUsed = (facetUsed == 'Regular') ? 'Mix' : 'Persistent';
            } else {
                facetUsed = (facetUsed == 'Persistent') ? 'Mix' : 'Regular';
            }
        }
        return facetUsed;
    }

    private getQueryTerms() {
        let queries = this.$stateParams['query'];
        let queryTerms = '';
        if (!angular.isArray(queries)) {
            queries = [queries];
        }
        for (let i=0;i<queries.length;i++) {
            let cur = queries[i];
            let splitted = cur.split(',');
            if (splitted.length >= 3) {
                queryTerms = queryTerms + splitted[0] + ' ' + splitted[1] + ' ' + splitted[2];
            }
            if (i<queries.length-1) {
                queryTerms = queryTerms + ' ' + splitted[splitted.length-1] + ' ';
            }
        }
        return queryTerms;
    }

    private getFieldSearchedFromUrl() {
        return this.getParamFromQuery(0);
    }

    private getSearchTypeFromUrl() {
        return this.getParamFromQuery(1);
    }

    private getSourceOfTheRecord() {
        let thisTab = getTab(this.getSearchScope()).toUpperCase();
        let scopes = window.appConfig['primo-view'].scopes;

        for (var scope in scopes) { //iterate over scopes until you find the scope that matches this tab...
            let tab = scopes[scope].tab.toUpperCase();
            let thisSearchScope = this.getSearchScope().toUpperCase();
            let scopeId = scopes[scope]['scope-id'].toUpperCase();
            if (tab === thisTab && thisSearchScope === scopeId) {
                let types = scopes[scope].types;

                if ((types.indexOf('local') > -1) && ((types.indexOf('adaptor') > -1) || (types.indexOf('deep_search') > -1))) {
                    return 'blended'; //  blended when the scope type contains 'local' and also one of the types 'adaptor' and 'deep_search'
                } else if ((types.indexOf('deep_search') > -1) || ((types.indexOf('local') === -1) && (types.indexOf('adaptor') > -1))) {
                    return 'deep_search'; // deep_search when the scope type contains 'deep_search' (for Suprima) or when it contains only 'adaptor' (for Primo)
                } else {
                    return 'local';// the scope type is 'local' when it contains only 'local'
                }
            }
        }
        return undefined;
    }

    private getSearchTab() {
        let searchTab = this.searchFieldsService.tab;
        if (!searchTab) {
            searchTab = this.$stateParams['tab'];
            if (!searchTab) {
                searchTab = this.searchStateService.getSearchObject().tab;
                if (!searchTab) {
                    searchTab = getTab(this.getSearchScope());
                }
            }
        }
        return searchTab;
    }

    private getSearchScope() {
        let searchScope = this.searchFieldsService.scope;
        if (!searchScope) {
            let searchScopeFromParam = this.$stateParams['search_scope']
            if (searchScopeFromParam) {
                searchScope = searchScopeFromParam;
            }
        }
        return searchScope;
    }

    private getParamFromQuery(i) {
        let queries = this.$stateParams['query'];
        if (queries) {
            queries = flatten([queries]);
            let param = queries.map((query) => {
                //arr contains elements from type 'any,contains,a,AND'
                let arr = query.split(',');
                if (i === 2) { // handling search term parameter
                    if (this.$stateParams['mode'] === 'advanced') {
                        if (arr.length > 4) { //advanced search and there was a comma in the query
                            return arr.slice(2, -1).join(",");
                        }
                    }
                    else if (arr.length > 3) { //simple search and there was a comma in the query
                        return arr.slice(2).join(",");
                    }
                }
                return arr[i];
            });
            return param;
        }
    }

    private getSearchTermsFromUrl() {
        let searchTermsFromUrl = this.getParamFromQuery(2);
        if (!searchTermsFromUrl) {
            return searchTermsFromUrl;
        }
        if (Array.isArray(searchTermsFromUrl)) {
            return searchTermsFromUrl.map(term => term ? term.replace(/&/g, '%26') : term);
        }
        return searchTermsFromUrl.replace(/&/g, '%26');
    }

    public showAfterRedirectAlert() {
        this.afterRedirectAlert.show('nui.message.fromredirect', false);
    }

    public hasBrowseGap() {
        let res = this.getResultObject();
        return (res != undefined && res.info != undefined && res.info.browseGap != undefined);
    }

    public resetSearchDone() {
        this.searchDone = false;
    }

    public isSearchDone() {
        return this.searchDone;
    }

    public getItemAtIndex(index) {
        let item = this.searchStateService.getItemAtIndex(index);
        if (item) {
            return item;
        }
        if (!this.searchStateService.isSearchInProgress()) {
            this.nextResults();
        }
    }

    public getRecommendationCount(recType) {
        return this.resourceRecommender.getRecommendationCount(recType);
    }

    public resetResultObject() {
        this.searchStateService.resetResultObject()
    }

    public resetCameFromParams() {
        this.cameFromParams = {};
    }

    /**
     * @ngdoc function
     * @name parseSearchResponse
     * @methodOf starter.service:searchService
     * @description This is the parser method that creates the following:
     * 1. searchService.result_object.data
     * 2. searchService.result_object.info
     * 3. searchService.result_object.details
     * 4. searchService.result_object.facets
     */

    public parseSearchResponse(response, replaceResults = false) {
        let res = this.searchResultParser.parseSearchResponse(response);
        if (!this.needToRender) {
            this.searchStateService.setTempResultList(res.groups);
        } else {
            this.searchStateService.setResultObject(res, replaceResults);
        }

        /*if MetaLib initial search then update the metaLibMore object to ignore all server more
        indication since it is fault - probably due to cached server responses*/
        if (_get(this.getSearchObject(), 'getMore', '') !== 1) {
            this.handleMetaLibMoreStatus(true);
        }
        /* facets */
        if (!this.needToRender) {
            this.facetService.setResult(res.facetsList);
        } else {
            this.facetService.setResult(this.getResultObject().facets);
        }
    }

    public performCitationTrailsSearch(facet) {
        let args = this.searchStateService.getSearchObject();
        args.citationTrailFilterByAvailability = !this.citationTrailsExpandButtonService.expandMyResultsForCitationTrail;
        args.offset = 0;
        args.facets = this.getFacetsFromUrl([facet], false);
        this.pageNavMenuService.pageInView = 1;
        this.searchStateService.resetResultObject();
        return this.search(args, true);
    }

    public performChaptersSearch(facets) {
        let args = this.searchStateService.getSearchObject();
        args.offset = 0;
        args.facets = this.getFacetsFromUrl(facets, true);
        args.isRelatedItems = true;
        this.searchStateService.resetResultObject();
        return this.search(args, true);
    }

    public performSearchInFulltextSearch(){
        let args = this.getSearchObject();
        args.searchInFulltextUserSelection = this.facetService.searchInFulltext;
        this.resetResultObject();
        return this.search(args, true);
    }

    public performCitationSearchInFulltextSearch(){
        let args = this.getSearchObject();
        args.citationTrailFilterByAvailability = !this.citationTrailsExpandButtonService.expandMyResultsForCitationTrail;
        args.offset = 0;
        this.pageNavMenuService.pageInView = 1;
        this.updateOffsetInUrl(0);
        this.removeCameFromPaginationInUrl();
        this.resetResultObject();
        return this.search(args, true);
    }

    /**
     * @ngdoc function
     * @name search
     * @methodOf starter.service:searchService
     * @description search wrapper
     */
    public search(args, isNewSearch) {
        args.sortby = this.setSortBy(args);
        args.getMore = args.getMore || 0;
        if (isNewSearch) {
            this.searchStateService.setSearchObject(args);
        } else {
            this.searchStateService.updateSearchObject(args);
        }


        //get search resulst mark all more

        if (_get(this.getSearchObject(), 'getMore', '') !== 1) {
            this.getSearchObject().metalibMoreStatus = {};
        } else {
            // We only want to update the more indication
            // if this is fetch more - for initial search we do it after the search results are parsed
            this.handleMetaLibMoreStatus(false);
        }

        return this._search();
    }

    private setSortBy(args) {
        if ((args.journals && args.journals.split(",")[0] === "letter") ||
            (args.databases && args.databases.split(",")[0] === "letter"))
            return "title";
        else
            return args.sortby || 'rank';
    }


    private handleMetaLibMoreStatus(isInitial) {
        let updatedResultbject = this.getResultObject();
        let data = _get(updatedResultbject, 'data', '');

        let results = data.map((result, index) => {
            let booster = _get(result, 'pnx.ranking.booster2');
            if (booster) {
                if (!this.getSearchObject().metalibMoreStatus) {
                    this.getSearchObject().metalibMoreStatus = {};
                }
                let boosterValue = ++booster[0];
                if (isInitial) {
                    this.getSearchObject().metalibMoreStatus = {};
                    boosterValue = 0;
                }
                this.getSearchObject().metalibMoreStatus[result.pnx.control.recordid[0]] = boosterValue;


            }
            return result;
        });
    }

    private setMessages(isGuest, isOffCampus) {

        let groupMsg: number = this.configurationUtil.getBriefResultConfiguration().groupmessage;
        let isToDisplay: boolean = this.configurationUtil.getBriefResultConfiguration().displaysigninmsg;
        //when the message should be displayed - configuration + state:
        if (isToDisplay) {
            if ((groupMsg === 0 && isGuest) || (groupMsg === 1 && isOffCampus)) {
                this._signInAlert.show('nui.signin_message', false);
            }
        }
    }


    public restSignInAlertMessage() {
        this._signInAlert.show('', false);
    }

    /**
     * @ngdoc function
     * @name loadResultsup
     * @methodOf starter.service:searchService
     * @description This method broadcasts that the search finished to the follwoing controllers:
     * 1. search-resul-list-controller
     * In addition it broadcasts the scroll.infiniteScrollComplete event to let the controllers know
     * that the ion-infinite-scroll had ended
     */
    public loadResults(data, replaceResults = false) {
        this.parseSearchResponse(data, replaceResults);
    }

    /**
     * @ngdoc function
     * @name nextResults
     * @methodOf starter.service:searchService
     * @description paging wrapper
     */
    public nextResults(replaceResults = false) {
        // all logic if to get next result must be handled in needToGetNextResults since the searchResult.directive.ts
        // is using same function (had to do it like that since beacon can't be called from a service)
        if (this.needToGetNextResults()) {
            this.searchStateService.nextBulk();
            this.getSearchObject().getMore = 0;
            return  (this.authoritySearchService.isAuthorityState() ? this.authoritySearchService.getNextRecords() : this._search(replaceResults, true)).then(() => {
                if (replaceResults) {
                    let newOffset = this.searchStateService.getSearchObject().offset;
                    if (newOffset != this.$stateParams['offset']) {
                        this.updateOffsetInUrl(newOffset);
                    }
                }
            });
        }
    }

    @preventStateReload
    updateOffsetInUrl(newOffset) {
        this.$location.search('offset', newOffset);
    }

    @preventStateReload
    removeCameFromPaginationInUrl() {
        if (this.$location.search().came_from && this.$location.search().came_from.startsWith("pagination")){
            this.$location.search('came_from', null);
        }
    }

    public jumpToPage(page) {
        // all logic if to get next result must be handled in needToGetNextResults since the searchResult.directive.ts
        // is using same function (had to do it like that since beacon can't be called from a service)
        this.searchStateService.jumpToPage(page);
        this._search();
    }

    /**
     * @ngdoc function
     * @name needToGetNextResults
     * @description check if needs to get next results.
     * all logic if to get next result must be handled here
     * since other laces might use this method too
     */
    private needToGetNextResults() {
        return this.searchStateService.hasNextBulk();
    }

    private needToGetPreviousResults() {
        return this.searchStateService.hasPreviosBulk();
    }


    /**
     * @ngdoc function
     * @name previousResults
     * @methodOf starter.service:searchService
     * @description paging wrapper
     */
    public previousResults(replaceResults = false) {

        if (this.needToGetPreviousResults()) {
            this.searchStateService.previousBulk();
            this.getSearchObject().getMore = 0;
            return (this.authoritySearchService.isAuthorityState() ? this.authoritySearchService.getPreviousRecords() : this._search(replaceResults, true)).then(() => {
                if (replaceResults) {
                    let newOffset = this.searchStateService.getSearchObject().offset;
                    if ((newOffset != this.$stateParams['offset']) || this.authoritySearchService.isAuthorityState()) {
                        this.updateOffsetInUrl(newOffset);
                    }
                }
            });
        }

    }

    public getSearchObject() {
        return this.searchStateService.getSearchObject();
    }

    public getResultObject() {
        return this.searchStateService.getResultObject();
    }

    public selectItem(item) {
        this.searchStateService.selectItem(item);
    }

    public getSelectedItem() {
        return this.searchStateService.getSelectedItem();
    }

    /**
     * @ngdoc function
     * @name addImagesResults
     * @methodOf starter.service:searchService
     * @description This method parses the images search results and updates the page
     */
    public addImagesResults(data) {
        this.parseImagesSearchResponse(data);
    }

    /**
     * @ngdoc function
     * @name parseImagesSearchResponse
     * @methodOf starter.service:searchService
     * @description This is the parser method that creates the following:
     * 1. searchService.result_object.images
     */

    public parseImagesSearchResponse(response) {
        let res = this.searchResultParser.parseImagesSearchResponse(response);
        this.searchStateService.addImagesToResultObject(res);
    }

    public isSearchInProgress() {
        return this.searchStateService.isSearchInProgress();
    }


    public getFrbrDisplay() {
        if (this.configurationUtil.getBriefResultConfiguration())
            return this.configurationUtil.getBriefResultConfiguration().frbrdisplay;
        else
            return 0;
    }


    public resetParameters(str = '') {


        let args = this.getSearchObject() || ({} as ISearchObject);
        this.getSearchObject().metalibMoreStatus = {};


        if (this.$stateParams['offset']) {
            args['offset'] = parseInt(this.$stateParams['offset'], 10);
        } else {
            args['offset'] = 0;
        }

        args.scope = this.searchUtil.urlParamScope || args.scope;
        args.tab = this.searchUtil.urlParamTab || args.tab;
        args.sortby = this.$stateParams['sortby'];
        args.explain = this.$stateParams['explain'];
        args.otbRanking = this.$stateParams['otbRanking'] || args.otbRanking;
        args.searchCDI = this.$stateParams['searchCDI'];
        args.savedSearchId = this.$stateParams['savedSearchId'];
        args.oSSKey = this.$stateParams['oSSKey'];
        args.rapido = isGTISearch();
        let facetsFromUrl = this.getFacetsFromUrl(this.$stateParams['facet'], false);
        // Get multiple facets
        args.facets = facetsFromUrl.concat(this.getFacetsFromUrl(this.$stateParams['mfacet'], true));
        args.isRelatedItems = this.$stateParams['isRelatedItems'];
        if(this.configurationUtil.isSuprima()){
            this.facetService.updateSearchInFulltext();
        }
        args.searchInFulltextUserSelection = this.facetService.searchInFulltext;
        args.citationTrailFilterByAvailability = !this.citationTrailsExpandButtonService.expandMyResultsForCitationTrail;

        if ((this.$state.current.name === STATE_NAMES.SEARCH || this.isNewsPapersSearch() || this.journalsService.isJournalSearch())
            && !(this.$stateParams['mode'] === "browse" || this.$stateParams['mode'] === "authority" || this.databasesService.isDBSearch())) {
            let stickyFacetsArray = this.facetService.getStickyFacets();
            if (stickyFacetsArray) {
                for (let stickyFacet of stickyFacetsArray) {
                    remove(args.facets, (facet) => this.facetService.isFacetEqual(facet, stickyFacet));
                    args.facets.unshift(stickyFacet);
                    this.addStickyFacetsToUrl(stickyFacet);
                }
            }
        }

        args.journals = this.$stateParams['journals'];
        args.databases = this.$stateParams['databases'];
        args.featuredNewspapersIssnList = this.$stateParams['issn'];
        args.fromDate = this.$stateParams['fromDate'];
        const previousPcAvailability = args.pcAvailability;
        args.pcAvailability = this.calcPcAvailability() || previousPcAvailability;
        this.updateStateParamsWithNewPcAvailabilityForNewsPapersSearch(args.pcAvailability, previousPcAvailability);
        args.searchInFulltextUserSelection = this.calcSearchInFullText() || args.searchInFulltextUserSelection
        args.browseParams = this.$stateParams['browseParams'];
        args.organization = this.$stateParams['organization'];
        args.originatingSystem = this.$stateParams['originatingSystem'];
        args.originatingSystemId = this.$stateParams['originatingSystemId'];
        args.isRAsearch = this.$stateParams['isRAsearch'];

        args.getMore = 0;
        let query = this.$stateParams['query'] || args.query;

        if (Array.isArray(query)) {
            args.query = join(query, ';');
            if (this.$stateParams['mode'] === 'simple') {
                args.query = query[0];
                this.updateQueryAfterSwitchingToSimpleMode(args);
            }
        } else {
            args.query = query;
            if (this.$stateParams['mode'] === 'simple') {
                this.updateQueryAfterSwitchingToSimpleMode(args);
            }
        }

        let mode = this.$stateParams['mode'];
        if (mode && mode === 'advanced') {
            args.mode = 'advanced';
            this.searchFieldsService.advancedSearch = true;
        } else {
            this.searchFieldsService.advancedSearch = false;
        }

        let pfilter = this.$stateParams['pfilter'];
        if (pfilter) {
            if (Array.isArray(pfilter)) {
                pfilter = this.normalizeCdatePrefilterToDr(pfilter);
                pfilter = join(pfilter, ';');
            }

            if (this.configurationUtil.isSuprima()) {
                if (pfilter) {
                    args.query += ';' + pfilter;
                    this.updatePreFiltersQueryAfterSwitchingToSimpleMode(query, args);
                }
            } else {
                let facetPfilter = pfilter.replace('lang', 'facet_lang')
                    .replace('pfilter', 'facet_pfilter')
                    .replace('creationdate', 'facet_creationdate');

                if (facetPfilter) {
                    args.query += ';' + facetPfilter;
                    this.updatePreFiltersQueryAfterSwitchingToSimpleMode(query, args);
                }
            }
        }

        if (_get(this, ['$stateParams', 'conVoc'], true) === 'false') {
            args['noConVoc'] = true;
        }
        let isAuthoritySearchAllowed:boolean = false;
        if (mode === 'authority') {
            isAuthoritySearchAllowed = args.originatingSystemId != undefined ;
        }
        if (query || isAuthoritySearchAllowed) {
            return this.search(args, true);
        }

    }

    @preventStateReload
    private addStickyFacetsToUrl(stickyFacet: any) {
        let stickyFacetStr = stickyFacet.name + ',' + stickyFacet.type + ',' + stickyFacet.value;
        let stickyMultiFacetStr = stickyFacet.isMultiFacet ? stickyFacetStr.concat(',' + stickyFacet.multiFacetGroupId + ',lk') : stickyFacetStr.concat(',lk');
        stickyFacetStr = stickyFacetStr.concat(',lk');

        // Make sure that we are working with an array of facets / mfacets
        this.$stateParams['facet'] = this.$stateParams['facet'] ? flatten([this.$stateParams['facet']]) : [];
        this.$stateParams['mfacet'] = this.$stateParams['mfacet'] ? flatten([this.$stateParams['mfacet']]) : [];

        // Change url by setting $location.search mfacet value according to :
        // If already include stickyMultiFacetStr --> Don't change anything, Else --> Add stickyMultiFacetStr
        if(stickyFacet.isMultiFacet){
            this.$stateParams['mfacet'] = this.$stateParams['mfacet'] && this.$stateParams['mfacet'].includes(stickyMultiFacetStr) ?
                this.$stateParams['mfacet'] : this.$stateParams['mfacet'].concat(stickyMultiFacetStr);
            this.$location.search('mfacet', this.$stateParams['mfacet']);
        }
            // Change url by setting $location.search facet value according to :
        // If already include stickyFacetStr --> Don't change anything, Else --> Add stickyFacetStr
        else{
            this.$stateParams['facet'] = this.$stateParams['facet'] && this.$stateParams['facet'].includes(stickyFacetStr) ?
                this.$stateParams['facet'] : this.$stateParams['facet'].concat(stickyFacetStr);
            this.$location.search('facet', this.$stateParams['facet']);
        }
    }

    //this is for backwards compatibility of links
    private normalizeCdatePrefilterToDr(pfilter) {
        if (this.configurationUtil.isSuprima()) {
            let foundStartingCdate = false;
            for (let i = 0; i < pfilter.length; i++) {
                if (pfilter[i].startsWith("cdate") && !foundStartingCdate) {
                    pfilter[i] = pfilter[i].replace("cdate", "dr_s");
                    foundStartingCdate = true;
                } else if (pfilter[i].startsWith("cdate") && foundStartingCdate) {
                    pfilter[i] = pfilter[i].replace("cdate", "dr_e");
                    break;
                }
            }
        }
        return pfilter;
    }

    private updateQueryAfterSwitchingToSimpleMode(args) {
        if (args.query === undefined) {
            return;
        }
        let queryArr = (args.query.toString()).split(',');
        if (['AND', 'OR', 'NOT'].indexOf(queryArr[queryArr.length - 1]) > -1) {
            queryArr = queryArr.slice(0, queryArr.length - 1);
            args.query = queryArr.join(',');
        }
    }

    private updatePreFiltersQueryAfterSwitchingToSimpleMode(query, args) {
        if (this.$stateParams['mode'] === 'simple') {
            if (Array.isArray(query)) {
                args.query = query[0];
            } else {
                args.query = query;
            }
            this.updateQueryAfterSwitchingToSimpleMode(args);
        }
    }

    public getFacetsFromUrl(facetStrings, isMultiFacets) {
        if (!facetStrings) {
            return [];
        }
        let facetStringsArr = flatten([facetStrings]);
        let facets = facetStringsArr.map((facetString) => {
            let isLocked = this.isCurrFacetLocked(facetString);
            if (isLocked) {    // Need to remove temporarily the ',lk' indication because it's irrelevant for the next logic of this function
                facetString = this.removeLockedIndication(facetString);
            }
            let facetArr = facetString.split(',');
            let multiFacetGroupId;
            if (isMultiFacets) {
                multiFacetGroupId = facetArr.pop();//in case the facet is multi facet, the last parameter is the group id
            }
            for (let i = 3; i < facetArr.length; i++) { //this is in case facet value has ',' characters
                facetArr[2] = facetArr[2] + ',' + facetArr[i];
            }
            facetArr[2] = he.decode(facetArr[2]);
            facetArr = facetArr.slice(0, 3);
            let displayedType = this.facetService.displayedTypeFacet[facetArr[0].replace('facet_', '', 1)] || 'exact';

            let displayableFacet = this.filterInstitution.filterAndNormalizeDataFacets([{
                name: facetArr[0],
                value: facetArr[2]
            }]);

            let val = facetArr[2];
            let displayValue: string;
            if (displayableFacet && displayableFacet.length > 0) {
                val = this.facetService.facetLabel(displayableFacet[0]);
                displayValue = displayableFacet[0].displayValue;
            }

            let displayLabel = this.facetService.getFacetLabelValue(facetArr[0], facetArr[2]);
            if (displayLabel) {//case the sever sends a display value
                displayValue = displayLabel;
            }

            let facetView = this.configurationUtil.getFacetConfiguration().facetview;
            let relevantFacet = facetView.filter((facet) => {
                return facet.id === facetArr[0]
            });
            let useTranslate = relevantFacet.length > 0 ? relevantFacet[0].useTranslations : false;

            let facet = {
                name: facetArr[0],
                type: facetArr[1],
                value: facetArr[2],
                displayValue: displayValue,
                displayedType: displayedType,
                label: '',
                multiFacetGroupId: multiFacetGroupId,
                isMultiFacet: isMultiFacets,
                tooltip: '',
                useTranslation: useTranslate,
                translationsPromise: undefined,
                isLocked: isLocked
            };
            //set a promise on the facet that will be resolved when all the facets label translations are done
            facet.translationsPromise = this.$q(async (resolve, reject) => {
                let label = await this.facetService.getFacetLabel(facetArr[0], displayedType, val);
                facet.label = label;
                let translation = await this.$translate(label, [], '', label);
                facet.label = translation;
                let prefix = this.$translate.instant('nui.facets.remove.tooltip');

                if (facetArr[0] === 'searchcreationdate') { //???
                    facetArr[0] = 'creationdate';
                }

                let typeName = this.$translate.instant('facets.facet.facet_' + facetArr[0]);
                if (this.configurationUtil.getBackendSystem() === 'suprima' && facetArr[0].startsWith("lds")) {
                    typeName = this.$translate.instant('fulldisplay.' + facetArr[0]);
                }
                let facetValue = (this.facetService.displayedTypeFacet[facet.name] === 'range') ?
                    facet.label :
                    this.translationService.translateInstantWithDefault(this.facetService.facetLabel(facet), this.facetService.getFacetDefaultValue(facet));

                facet.tooltip = (facet.name !== 'frbrgroupid') ? prefix + typeName + ' ' + facetValue : prefix + typeName;
                resolve(facet); //signal that all translations are done for this facet
            });

            // Need to add back the ',lk' indication and do lock and fill to the relevant active facet
            if (facet.isLocked) {
                this.facetService.addStickyFacet(facet);
            }
            return facet;
        });
        return facets;
    }

    private isCurrFacetLocked(facetString): boolean {
        return facetString && facetString.endsWith(',lk');
    }

    private removeLockedIndication(facetString) {
        return facetString.replace(',lk', '');
    }

    public get numOfLoadedResultBulks(): number {
        return this.searchStateService.numOfLoadedResultBulks;
    }


    private init() {
        this.resetParameters();
    }


    private reloadSearchAfterSLI() {
        if (this.reloadSearch) {
            this.reloadSearch = false;
            this.$state.reload();
        } else {
            this.reloadSearch = true;
        }
    }

    //this function causes the search state to not be reloaded by router when we change the url search parameters
    private preventSearchStateReload() {
        let searchState = this.$state.current;
        //this wiil cause the router to not reload the search state when we change the search params in the url
        searchState.reloadOnSearch = false;

    }

    /**
     * @ngdoc function
     * @name _search
     * @methodOf starter.service:searchService
     * @description search wrapper
     */
    private _search(replaceResults, isNextResultSearch) {
        this.cheetah.firstAuthRequest = true;
        this.preventSearchStateReload();
        this.$rootScope.$on('silentLogin', () => {
            this.reloadSearchAfterSLI()
        });

        this.searchDone = true;
        let skipDelivery = this.configurationUtil.isSuprima() && !getPrimoVeSkipDelivery() ? 'N' : 'Y';
        this.searchStateService.setSkipDelivery(skipDelivery);
        let searchObject = this.getSearchObject();
        this.searchObjectBuffer.push(searchObject);
        this.searchStateService.setSearchInProgress(true);
        this.searchBar.setShowSignIn(false);
        let resultObject = this.getResultObject();
        this.expandedSearchAfterZeroResults = false;

        // need cleaner solution for limit and imagesBarLimit
        //if(this.$state.params['mode']=== 'browse') {
        //	searchObject.scope = "browse_search";
        //}

        let searchOffset = searchObject.offset | 0;
        let blendFacetsSeparately = this.blendFacetsSeparatelyConf && this.cheetah.isScopeBlended(searchObject.scope) && searchOffset === 0;
        searchObject['blendFacetsSeparately'] = blendFacetsSeparately;


        window.appPerformance.timeStamps['before search api'] = Date.now();
        let p = this.cheetah.performSearch(searchObject, resultObject.info, true).then(
            (response: { data: PrimoResponse.ISearchResponse }) => {
                if (this.configurationUtil.isSuprima() && !(this.$stateParams['mode'] === "authority")) {
                    searchObject['facetsCacheKey'] = response.data['facetsCacheKey'];
                    this.getFacetsRetry(response, searchObject, resultObject);
                }
                if (blendFacetsSeparately&&!searchObject.isIgnoreFacetSearch) {
                    let originalFacets = this.searchResultParser.parseSearchResponse(response.data).facetsList;
                    response.data.facets = [];
                    setTimeout(() => {
                        searchObject['blendFacetsSeparately'] = false;
                        this.cheetah.performSearch(searchObject, resultObject.info, true, true).then((response: { data: PrimoResponse.ISearchResponse }) => {
                            let result = this.searchResultParser.parseSearchResponse(response.data);
                            this.facetService.setResult(result.facetsList);
                        }, (err) => {
                            //if there was a failure in the second call use the facets from the first call
                            this.facetService.setResult(originalFacets);
                        });
                    }, 650);
                }
                if (!this.configurationUtil.isSuprima()) {
                    let deliveryServiceUrl = this.restBaseURLs.pnxBaseURL + '/delivery?'
                        + this.$httpParamSerializer(this.cheetah.searchData);

                    let recordsDataForPrimoEdelivery = [];
                    this.$http.post(deliveryServiceUrl, {
                        "records": response.data.docs
                    }).then(
                        (dResponse) => {
                            let data: PrimoResponse.ISearchResponse = dResponse.data;
                            dResponse.data.docs.forEach((doc, index) => {
                                response.data.docs[index].delivery = doc.delivery;
                                data.docs[index].context = response.data.docs[index].context;
                                if (doc['@rta_link'] && dResponse.data.docs[index]['@rta_link']) {
                                    response.data.docs[index]['@rta_link'] = dResponse.data.docs[index]['@rta_link'];
                                    data.docs[index]['@rta_link'] = dResponse.data.docs[index]['@rta_link'];
                                }

                                // Prepare records for edelivery call (journal coverage addition)
                                recordsDataForPrimoEdelivery = recordsDataForPrimoEdelivery.concat(this.prepareRecordsForEdeliveryCall(doc));

                            });
                            let dmTimer = data.timelog.FullEndDMDataDeltaTime;
                            if (dResponse.data.rtaResultsLink) {
                                response.data.rtaResultsLink = dResponse.data.rtaResultsLink;
                                data.rtaResultsLink = dResponse.data.rtaResultsLink;
                            }
                            window.appPerformance.serverTimeLog['FullEndDMDataDeltaTime'] = dmTimer;
                            if (this.configurationUtil.getLocationsConfiguration() &&
                                this.configurationUtil.getLocationsConfiguration().rta !== 'none') {
                                this.rtaService.updateAvailability(data.docs);
                            }

                            // We will call alma only if we have records for edelivery calculation and "brief_results_journal_coverage" parameter is enabled
                            if (recordsDataForPrimoEdelivery && recordsDataForPrimoEdelivery.length > 0 && this.isbriefResultsJournalCoverageEnabledInPrimo()) {
                                this.primoDeliveryBulkCall(recordsDataForPrimoEdelivery, dResponse.data.docs);
                            }

                        });
                } else if (this.searchStateService.getSkipDelivery() === 'Y' && !(this.$stateParams['mode'] === "authority")) {

                    let mmsIds = response.data.docs
                        .map((doc) => doc.pnx.control.recordid[0])
                        .filter(recordId => recordId.match(/^alma\d+$/));
                    let rapidoMmsIds = response.data.docs
                        .map((doc) => _get(doc, 'pnx.control.rapidosourcerecordid'))
                        .filter(recordId => recordId !== undefined).toString();
                    let networkMmsIds = response.data.docs
                        .map((doc) => _get(doc, 'pnx.control.networklinkedrecordid'))
                        .filter(recordId => recordId !== undefined);

                    this.getRecordDelivery(mmsIds, rapidoMmsIds, networkMmsIds, response.data.docs);
                }

                this.beaconResultsNumber = _get(response, 'data.info.total');
                this.reloadSearch = false;
                window.appPerformance.timeStamps['after search api'] = Date.now();
                let data: PrimoResponse.ISearchResponse = response.data;
                window.appPerformance.serverTimeLog = data.timelog;
                let searchState = this.$state.current; //get the configuration of the search state from the router
                searchState.reloadOnSearch = true; //return reload on search to true so that back button in browser works


                this.recordUtil.loadRecordsExtras(data.docs);

                /*
                 * Notice that loadResults(data); in its deep call update the newSearch to false
                 * This happens in setResultObject(searchResponse) API in SearchStateService
                 * the handleSnippet API must be called before the loadResults API
                 */
                if (!this.$stateParams['journals'] && !this.$stateParams['databases'] && !this.$stateParams['browseScope']) {
                    this.snippetService.updateSnippets(data.docs, data.highlights);
                }

                if (this.quickLinkService.isUnpaywallLinksAvailable(data.docs)) {
                    this.quickLinkService.updateUnpaywallLinks(data.docs);
                }

                //this.timesCitedService.updateCitedTimesAvailability(data.docs);
                if (this.checkDuplicateAZSearch(response)) {
                    return this._search(replaceResults, isNextResultSearch);
                }
                this.loadResults(data, replaceResults);
                this.searchStateService.setSearchInProgress(false);

                /*
                Highlights service
                */
                this.highlightsService.updateHighlights(data.highlights);

                let isGuest = this.jwtUtilService.isGuest();
                let isOffCampus = this.jwtUtilService.getDecodedToken().onCampus !== 'true';

                if (isGuest) {
                    this.setMessages(isGuest, isOffCampus);
                }
                this._browseGapAlert.show('nui.browseGap_message', false);
                this.afterRedirectAlert.hide();
                updateZoteroPlugin(); //send event so that zotero plugin identifies new results
                if (response.data.expandedSearchAfterZeroResults){
                    this.expandedSearchAfterZeroResults = true;
                    this.facetService.pcAvailability = true;
                    this.$location.search('pcAvailability', null);
                }

            },
            (response) => {
                let searchState = this.$state.current; //get the configuration of the search state from the router
                searchState.reloadOnSearch = true; //return reload on search to true so that back button in browser works

                let err = response.data;
                if (!err) {
                    return;
                }
                let beaconSearchErrorMessage = "Error code: " + (response.status || err.errorCode)
                    + ", Error message: " + err.errorMessage;//to add to failed search beacon calls
                beaconSearchErrorMessage = beaconSearchErrorMessage.substring(0, 100);
                let emptyResult = {
                    docs: [],
                    facets: [],
                    info: {
                        first: '1',
                        last: '0',
                        total: '0',
                        beaconSearchErrorMessage: beaconSearchErrorMessage,
                        errorDetails: {errorCode: this.searchErrorMessageService.serverError}
                    },
                };
                if (this.restrictedOffsetHandlerService.isRestrictedOffsetError(err)) {
                    this.restrictedOffsetHandlerService.handleRestrictedOffsetError().then((result) => {
                       switch (result) {
                           case HandleLimitOffsetErrorResult.RETRY:
                                 this._search(replaceResults, isNextResultSearch);
                                 break;
                           case HandleLimitOffsetErrorResult.USER_LOGGED_IN:
                               // check if we need to run search again after user logged in
                               break;
                           case HandleLimitOffsetErrorResult.ERROR:
                               const lastViewedOffset = this.searchStateService.getLastViewedOffset();
                               if (this.searchStateService.numOfLoadedResultBulks === 0) {
                                   // Restore original offset in URL, trigger search
                                   this.$state.go(this.$state.current.name, { offset: lastViewedOffset });
                                   this.searchStateService.setOffsetLimitNotificationVisible(true, true);
                               } else {
                                   // We already have some results, so no need to retrigger search
                                   // Just restore original offset in URL
                                   this.updateOffsetInUrl(lastViewedOffset);
                                   this.searchStateService.setOffsetLimitNotificationVisible(true);
                                   this.getSearchObject().offset = lastViewedOffset;
                                   this.searchStateService.setSearchInProgress(false);
                               }
                               break;
                       }
                    });
                    return;
                }
                if (err.errorMessage === 'Restricted scope'){
                    err.errorMessage = 'Not Authorized';
                    emptyResult.info.errorDetails.errorCode = this.searchErrorMessageService.notAuthorized;
                }
                if (this.checkDuplicateAZSearch(response)) {
                    return this._search(replaceResults, isNextResultSearch);
                }
                this.loadResults(emptyResult);
                this.searchStateService.setSearchInProgress(false);
                if (err.errorMessage === 'Not Authorized') {
                    this.reloadSearchAfterSLI();
                    this.searchBar.setShowSignIn(true);
                    this._signInAlert.hide();
                }
            }
        ).then((res) => {
            if(!(this.$stateParams['mode'] === "browse" || this.$stateParams['mode'] === "authority"  || this.journalsService.isJournalSearch() || this.databasesService.isDBSearch() ||
                this.newspapersService.isNewspapersSearch() || this.$state.current.name === STATE_NAMES.CITATION_TRAILS
                || (this.$stateParams['facet'] && this.$stateParams['facet'].indexOf('frbrgroupid') > -1))){
                this.searchHistoryService.updateSearchHistory(); //saves the current search query URL to the browser, on the local forage, after every search action (of any type).
            }
        });
        return p;
    }

    public getRecordDelivery(mmsIds: string[], rapidoMmsIds: string, networkMmsIds:string[], docs: IDoc[]) {
        if ((mmsIds && mmsIds.length) || rapidoMmsIds) {

            let {url, params} = this.getUrlAndParams(docs, rapidoMmsIds, mmsIds);

            let recordsDataForEdelivery = [];

            return this.$http.post(url, params
            ).then(
                (dResponse) => {
                    docs.forEach((doc) => {
                        let dataToAdd = dResponse.data
                            .filter(item => item.pnx.control.recordid[0].match(doc.pnx.control.recordid) || (doc.pnx.control.rapidosourcerecordid && item.pnx.control.recordid[0].match(doc.pnx.control.rapidosourcerecordid)));
                        if (dataToAdd.length) {
                            let electronicServices;
                            if(doc.delivery && doc.delivery.electronicServices){
                                electronicServices = doc.delivery.electronicServices;
                            }
                            doc.delivery = dataToAdd[0].delivery;
                            if(doc.delivery && !doc.delivery.electronicServices && electronicServices){
                                doc.delivery.electronicServices = electronicServices;
                            }
                            doc.enrichment = dataToAdd[0].enrichment;
                            this.deliveryPnxUpdatedFields.forEach((field) => {
                                this.updateFieldinPnxFromDelivery(field, dataToAdd[0], doc);
                            });
                            if (!doc.pnx.control.colldiscovery && dataToAdd[0].pnx.control.colldiscovery) {
                                doc.pnx.control.colldiscovery = dataToAdd[0].pnx.control.colldiscovery;
                                doc.delivery.cdUpdate = doc.delivery.cdUpdate || {};
                                doc.delivery.cdUpdate.colldiscovery = dataToAdd[0].pnx.control.colldiscovery;
                            }
                            if (!doc.pnx.display.cdparentid && dataToAdd[0].pnx.display.cdparentid) {
                                doc.pnx.display.cdparentid = dataToAdd[0].pnx.display.cdparentid;
                                doc.delivery.cdUpdate = doc.delivery.cdUpdate || {};
                                doc.delivery.cdUpdate.cdparentid = dataToAdd[0].pnx.display.cdparentid;
                            }

                            if (doc.delivery && doc.delivery.deliveryCategory && (doc.delivery.deliveryCategory.includes("Alma-E")
                                || doc.delivery.deliveryCategory.includes("Alma-D")
                                || doc.delivery.deliveryCategory.includes("Remote Search Resource"))) {

                                let recordData = {
                                    recId: doc.pnx.control.recordid[0],
                                    sharedDigitalCandidates: doc.delivery.sharedDigitalCandidates
                                };
                                recordsDataForEdelivery.push(recordData);
                            }
                        }
                    });
                    if(networkMmsIds && networkMmsIds.length) {
                        this.getRecordDeliveryForNetwork(networkMmsIds, docs);
                    }

                    if(mmsIds && mmsIds.length){
                        this.setMissingDeliveryAlmaInstitutions(mmsIds, docs);
                    }

                    if (this.configurationUtil.isSuprima() && getPrimoVeDeliveryBulkFromBrief()) {
                        this.deliveryBulkCall(recordsDataForEdelivery, docs);
                    }

                });
        } else if(networkMmsIds && networkMmsIds.length){
            this.getRecordDeliveryForNetwork(networkMmsIds, docs);
        }
    }

    private getUrlAndParams(docs: IDoc[], rapidoMmsIds: string | (string & any[]), mmsIds: string[]) {
        if (rapidoMmsIds && !window.appConfig['system-configuration'].temp_rs_stop_using_rapido_delivery_in_discovery) {
            let url = this.restBaseURLs.openUrl + "/rapidoDelivery/" + getInstitutionCode() + "/" + this.vid;
            return {url, params: docs};
        }
        let url = this.restBaseURLs.deliveryURL + "?" + this.$httpParamSerializer(this.cheetah.searchData);
        let rapidoArray: string[] = [];
        if (rapidoMmsIds) {
            rapidoArray = (Array.isArray(rapidoMmsIds) ? rapidoMmsIds : rapidoMmsIds.split(','))
                .map(id => id.startsWith('alma') ? id : `alma${id}`);
        }

        let params = [...mmsIds, ...rapidoArray];
        return {url, params};
    }

    private getRecordDeliveryForNetwork(networkMmsIds: string[], docs: IDoc[]){
        let prefix = "alma";
        networkMmsIds = networkMmsIds.map(id => prefix+id);
        let url, params, beginIndex, endIndex;
        url = this.restBaseURLs.deliveryURL + "?" + this.$httpParamSerializer(this.cheetah.searchData);
        beginIndex = url.indexOf("scope");
        endIndex = url.indexOf("&", beginIndex);
        url = url.substring(0, beginIndex) + "scope=DiscoveryNetwork" + url.substring(endIndex);
        params = networkMmsIds;
        return this.$http.post(url, params
        ).then(
            (dResponse) => {
                let filteredResponse = dResponse.data.filter(item => item.delivery && item.delivery.almaInstitutionsList && item.delivery.almaInstitutionsList.length > 0);
                docs.forEach((doc) => {
                    if(doc.pnx.control.networklinkedrecordid){
                        let filteredData = filteredResponse.filter(item => item.delivery.almaInstitutionsList[0].getitLink[0].linkRecordId.match(doc.pnx.control.networklinkedrecordid));
                        this.setDeliveryAlmaInstitutions(filteredData, doc);
                    } else {
                        let filteredData = filteredResponse.filter(item => item.delivery.almaInstitutionsList[0].getitLink[0].linkRecordId.match(doc.pnx.control.sourcerecordid));
                        this.setDeliveryAlmaInstitutions(filteredData, doc);
                    }
                });
            },(error)=>{
                console.log("ERROR:" + error);
            });
    }

    private setMissingDeliveryAlmaInstitutions(mmsIds: string[], docs: IDoc[]){
        let url = this.restBaseURLs.deliveryURL + "?" + this.$httpParamSerializer(this.cheetah.searchData);
        return this.$http.post(url, mmsIds
        ).then(
            (dResponse) => {
                let filteredResponse = dResponse.data.filter(item => item.delivery && item.delivery.almaInstitutionsList && item.delivery.almaInstitutionsList.length > 0);
                docs.forEach((doc) => {
                    if(doc.pnx.control.recordid[0]){
                        let filteredData = filteredResponse.filter(item => item.pnx.control.recordid[0].match(doc.pnx.control.recordid));
                        this.setDeliveryAlmaInstitutions(filteredData, doc);
                    }
                });
            },(error)=>{
                console.log("ERROR:" + error);
            });
    }

    private setDeliveryAlmaInstitutions(filteredData, doc){
        if(filteredData && filteredData[0]){
            if(doc.delivery){
                doc.delivery.almaInstitutionsList =  filteredData[0].delivery.almaInstitutionsList;
            } else{
                doc.delivery = {};
                doc.delivery.almaInstitutionsList =  filteredData[0].delivery.almaInstitutionsList;
            }
        }
    }

    deliveryBulkCall(recordsDataForEdelivery, docs) {
        let payload = {};
        payload = recordsDataForEdelivery;

        let url = this.restBaseURLs.prepareElectorincRTA + '?vid=' + this.vid + '&' + this.$httpParamSerializer(this.cheetah.searchData);

        this.$http.post(url, payload).then(
            (response) => {
                docs.forEach((doc) => {
                    if (response.data[doc.pnx.control.recordid]){
                        this.fullViewService.mergeDeliveryWithEDelivery(doc, response.data[doc.pnx.control.recordid], doc.delivery.electronicServices !== null);
                        this.fullViewService.calculatePrimaViewItDeliveryDone = true;
                        doc.delivery.edeliveryCalculated = true;
                        window.appPerformance.serverTimeLog['viewit'] = response.data.beaconO22;
                    }
                });
            });

    }

    // This consolidated coverage should only be displayed for local journals in the brief results, full display and Journal Search interface
    prepareRecordsForEdeliveryCall(doc){

        let tempArr = [];

        // For PRM-46199 : Verify we display coverage only for type='journal' records
        if(!(_get(doc, 'pnx.display.type[0]').toLowerCase() === 'journal')){
            return tempArr;
        }

        if(this.isbriefResultsJournalCoverageEnabledInPrimo()){
            let mmsIds = _get(doc, 'pnx.search.addsrcrecordid');
            let delCategories = _get(doc, 'delivery.deliveryCategory');
            if(mmsIds && delCategories &&
                (delCategories.includes("Alma-E")
                    || delCategories.includes("Alma-D")
                    || delCategories.includes("Online Resource")
                    || delCategories.includes("Remote Search Resource"))) {
                mmsIds.forEach((mmsid) => {
                    let recordData = {recId: mmsid};
                    tempArr.push(recordData);
                });
            }
        }
        return tempArr;
    }

    primoDeliveryBulkCall(recordsData, docs){

        let payload = {}
        payload = recordsData;

        let searchData = !this.$httpParamSerializer(this.cheetah.searchData)? 'lang='+this.$translate.use() : this.$httpParamSerializer(this.cheetah.searchData);
        let url = this.restBaseURLs.openUrl + '/edelivery?' + 'rfr_id=info:sid/primo.exlibrisgroup.com:primo4-journal-cLinker&svc_dat=CTO' + '&' + searchData;
        let chunkedPayload  = chunk(recordsData, 9);

        for (let i = 0; i < chunkedPayload.length; i++) {
            setTimeout(() => {
                this.$http.post(url, chunkedPayload[i]).then((edeliveryResponseRecords) => {
                    docs.forEach((doc) => {
                        let mmsIds = _get(doc, 'pnx.search.addsrcrecordid');
                        if(mmsIds){
                            mmsIds.forEach((mmsid) => {
                                // Adding consolidate_coverage for each doc existing delivery section
                                if (edeliveryResponseRecords.data && edeliveryResponseRecords.data[mmsid]){
                                    let edelivery = edeliveryResponseRecords.data[mmsid];
                                    // Now we can add consolidated coverage
                                    if(edelivery !== "" && edelivery.delivery.consolidatedCoverage !== ""){
                                        doc.delivery.consolidatedCoverage = edelivery.delivery.consolidatedCoverage;
                                    }
                                }
                            });
                        }
                    });
                });
            } , i*1000);
        }
    }

    public updateFieldinPnxFromDelivery(field, updated, target) {
        let updatedValue = _get(updated, field);
        if (updatedValue) {
            _set(target, field, updatedValue);
        }
    }

    private checkDuplicateAZSearch(response): boolean {
        //PRM-41653 - if swiching between letters too fast - need to search again
        let azType = this.journalsService.isJournalSearch() ? 'journals' : (this.databasesService.isDBSearch() ? 'databases' : undefined);
        if (azType) {
            if (this.searchObjectBuffer.length > 1) {
                this.searchObjectBuffer = [];
                return true;
            } else {
                let responseQuery: string;
                let responseLetter: string;
                let searchObjQuery: string;
                let searchObjLetter: string;

                responseQuery = _get(response, `config.params.${azType}`);
                if (responseQuery && responseQuery.split(',')[0] === 'letter') {
                    responseLetter = responseQuery;
                }
                searchObjQuery = _get(this.searchObjectBuffer.pop(), azType);
                if (searchObjQuery && searchObjQuery.split(',')[0] === 'letter') {
                    searchObjLetter = searchObjQuery;
                }
                if (searchObjLetter && responseLetter && searchObjLetter != responseLetter) {
                    return true;
                }
            }
        }
        return false;
    }


    public IsScopeLocal(scope?) {
        let scopesContext;
        if (scope)
            scopesContext = window.appConfig['scopes-context-map'][scope];
        else if (this.$stateParams['mode'] === "browse" || this.$stateParams['mode'] === "authority" )
            scopesContext = "L";
        else if (this.searchFieldsService.scope)
            scopesContext = window.appConfig['scopes-context-map'][this.searchFieldsService.scope];
        else
            scopesContext = "";
        return (scopesContext === "L");
    }


    public calcPcAvailability() {
        if (this.$state.current.name === STATE_NAMES.CITATION_TRAILS) {
            return true;
        }
        let stateParamsAvailability = this.$stateParams['pcAvailability'];
        if (stateParamsAvailability !== undefined) {
            return stateParamsAvailability;
        }
        if (this.isNewsPapersSearch()){
            return getNewspaperSearchFilterByAvailability() ? "false" : "true";
        }
        let scope = this.$stateParams['search_scope'];
        let availabilityForScope = this.facetService.showPcAvailabilityForScope(scope);
        // this.facetService.showPcAvailabilityForScope() returns true when "filter by availability" is checked;
        // in this case the pcAvailability search parameter must be false.
        // that's why the return value from showPcAvailabilityForScope() is inverted
        return !availabilityForScope;
    }

    public calcSearchInFullText() {
        if (this.$state.current.name === STATE_NAMES.CITATION_TRAILS) {
            return true;
        }

        let scope = this.$stateParams['search_scope'];
        if (this.isNewsPapersSearch()) {
            scope = this.configurationUtil.isSuprima() ? "CentralIndex" : "PC";
        }

        return this.$stateParams['searchInFulltext'] ||
            !this.facetService.showSearchInFullTextForScope(scope);
    }

    public updatePageTitle() {
        let oneWordQuery = this.searchFieldsService.mainSearch;
        let actionToCode = {
            'exploreMain.jsearch': !this.isSuprima() ? ['nui.mainmenu.label.journalsearch', '$$QUERY$$'] : ['mainmenu.label.JournalSearch', '$$QUERY$$'],
            'exploreMain.dbsearch': !this.isSuprima() ? ['nui.mainmenu.label.databasesearch', '$$QUERY$$'] : ['mainmenu.label.DatabaseSearch', '$$QUERY$$'],
            'exploreMain.npsearch': !this.isSuprima() ? ['nui.mainmenu.label.newspapers_search', '$$QUERY$$'] : ['mainmenu.label.NewspaperSearch', '$$QUERY$$'],
            'exploreMain.search': !this.isSuprima() ? ['main.title', '$$QUERY$$'] : ['main.title', '$$QUERY$$'],
        };
        let titles = actionToCode[this.$state.current.name] ? actionToCode[this.$state.current.name] : actionToCode['exploreMain.search'];
        this.pagesTitlesService.updatePagesTitles({codes: titles, query: oneWordQuery});
    }

    public updatePageDescription() {
        try {
            this.$translate('main.description').then((translations) => {
                // set the page title from label
                let descTag = <HTMLMetaElement>document.getElementsByName("description")[0];
                let descOgTag = <HTMLMetaElement>document.getElementById("ogDescription");
                descTag.content = translations;
                descOgTag.content = translations;
            });
        } catch (e) {
            console.log("translation of description is corrupt");
        }

    }


    public updatePageTitleFullView(recordTitle, institutionCode) {
        try {
            this.$translate(institutionCode).then((translatedInstCode) => {
                let placeHolders = {recordTitle: recordTitle, institutionName: translatedInstCode};
                this.$translate(['nui.full.display.title'], placeHolders).then((translations) => {
                    // set the page title from label
                    let titleElement = <HTMLLinkElement>document.getElementById('primoExploreTitle');
                    let titleOgElement = <HTMLMetaElement>document.getElementById('ogTitle');
                    let title = translations['nui.full.display.title'];
                    title = title.replace(/\s*\-\s*$/, "");
                    titleElement.innerHTML = title;
                    titleOgElement.content = title;

                });
            });
            this.updateOgURL();
        } catch (e) {
            console.log("translation of 'nui.brief.title' is corrupt");
        }

    }

    public updatePageTitleResearch() {
        try {
            let institutionCode = getInstitutionCode();
            this.$translate(institutionCode).then((translatedInstCode) => {
                let placeHolders = {institutionName: translatedInstCode};
                this.$translate(['nui.brief.title.research'], placeHolders).then((translations) => {
                    // set the page title from label
                    let titleElement = <HTMLLinkElement>document.getElementById('primoExploreTitle');
                    let titleOgElement = <HTMLMetaElement>document.getElementById('ogTitle');
                    let title = translations['nui.brief.title.research'];
                    title = title.replace(/\s*\-\s*$/, "");
                    titleElement.innerHTML = title;
                    titleOgElement.content = title;
                });
            });
            this.updateOgURL();
        } catch (e) {
            console.log("translation of 'nui.brief.title.research' is corrupt");
        }
    }

    public updateCanonicalFullDisplayResearch() {
        try {
            let canonicalFullDisplay = <HTMLLinkElement>document.getElementById('canonicalFullDisplay');
            canonicalFullDisplay.href = window.location.href;
            let ogURL = <HTMLMetaElement>document.getElementById('ogURL');
            ogURL.content = window.location.href;
        } catch (e) {
            console.log("Not exists meta element canonicalFullDisplay in index");
        }
    }

    public updatePageDescriptionResearch() {
        try {
            let institutionCode = getInstitutionCode();
            this.$translate(institutionCode).then((translatedInstCode) => {
                let placeHolders = {institutionName: translatedInstCode};
                this.$translate(['nui.brief.description.research'], placeHolders).then((translations) => {
                    // set the page Description from label
                    let recordDescription = translations['nui.brief.description.research'];

                    let descTag = <HTMLMetaElement>document.getElementsByName("description")[0];
                    let descOgTag = <HTMLMetaElement>document.getElementById("ogDescription");
                    descTag.content = recordDescription;
                    descOgTag.content = recordDescription;
                });
            });
        } catch (e) {
            console.log("translation of 'nui.brief.description.research' is corrupt");
        }
    }

    public updatePageDescriptionFullView(recordDescription) {
        if (recordDescription == "") {
            this.updatePageDescription();
        } else {
            try {
                let descTag = <HTMLMetaElement>document.getElementsByName("description")[0];
                let descOgTag = <HTMLMetaElement>document.getElementById("ogDescription");
                descTag.content = recordDescription;
                descOgTag.content = recordDescription;
            } catch (e) {
                console.log("Not exists meta element description in index");
            }
        }

    }

    public updateOgURL() {
        try {
            let canonicalFullDisplayUrl = this.getCanonicalFullDisplayUrl();
            if (canonicalFullDisplayUrl) {
                let ogURL = <HTMLMetaElement>document.getElementById('ogURL');
                ogURL.content = canonicalFullDisplayUrl;
            }

        } catch (e) {
            console.log("Not exists meta element URL in index");
        }

    }

    public updateOgImage(imageIrl) {
        try {
            let imageOgTag = <HTMLMetaElement>document.getElementById("ogImage");
            imageOgTag.content = imageIrl;
        } catch (e) {
            console.log("Not exists meta element description in index");
        }

    }

    public clearPageTitle() {
        let titleElement = <HTMLLinkElement>document.getElementById('primoExploreTitle');
        let titleOgElement = <HTMLMetaElement>document.getElementById('ogTitle');
        titleElement.innerHTML = "";
        titleOgElement.content = "";
        this.clearResetURL();

    }

    public clearPageDescription() {
        this.updatePageDescriptionFullView("");
    }

    public clearResetURL() {
        let ogURL = <HTMLMetaElement>document.getElementById('ogURL');
        ogURL.content = window.location.origin;
    }

    public resetImageOf() {
        let logoElement = <HTMLMetaElement>document.getElementById('logoImage');
        let logoUrl = logoElement.src;
        this.updateOgImage(logoUrl);

    }

    public updatePageKeywordsFullView(recordDescription) {
        try {
            let x = <HTMLMetaElement>document.getElementsByName("keywords")[0];
            x.content = recordDescription;
        } catch (e) {
            console.log("Not exists meta element keywords in index");
        }

    }

    public getRecordId(itemControl) {
        let recordId = itemControl.recordid[0];
        if (recordId.startsWith('dedupmrg') || recordId.startsWith('TN_dedupmrg')) {
            let originalRecordId = itemControl.sourcerecordid[0];
            let index = originalRecordId.indexOf('$$O');
            if (index !== -1) {
                originalRecordId = originalRecordId.substring(index + 3);
            }
            return originalRecordId;
        }
        return recordId;


    }


    public getRestToSDOType(type) {
        switch (type) {
            case 'article':
                return 'Article';
            case 'audio':
                return 'CreativeWork';
            case 'book':
                return 'Book';
            case 'bookChapter':
                return 'Chapter';
            case 'collection':
                return 'CreativeWork';
            case 'conference_proceeding':
                return 'Article';
            case 'database':
                return 'CreativeWork';
            case 'dissertation':
                return 'Thesis';
            case 'GovernmentDocument':
                return 'Book';
            case 'image':
                return 'Photograph';
            case 'journal':
                return 'Periodical';
            case 'kit':
                return 'CreativeWork';
            case 'LegalDocument':
                return 'Book';
            case 'manuscript':
                return 'Book';
            case 'map':
                return 'Map';
            case 'newspaper':
                return 'Periodical';
            case 'NewspaperArticle':
                return 'NewsArticle';
            case 'other':
                return 'CreativeWork';
            case 'ReferenceEntry':
                return 'CreativeWork';
            case 'ResearchDataset':
                return 'Dataset';
            case 'Review':
                return 'Review';
            case 'score':
                return 'CreativeWork';
            case 'StatisticalDataSet':
                return 'CreativeWork';
            case 'TechnicalReport':
                return 'Report';
            case 'text_resource':
                return 'CreativeWork';
            case 'video':
                return 'Movie';
            case 'website':
                return 'WebSite';
            case 'print_book':
                return 'Book';
            case 'book_chapter':
                return 'Chapter';
            case 'report':
                return 'Report';
            case 'journal_article':
                return 'Article';
            case 'Conference-Paper':
                return 'Article';
        }
    }


    public getScheameDotOrg(item, title) {
        let schema = {};
        schema['@context'] = 'http://schema.org';
        schema['@type'] = 'WebPage';

        // permalink add location
        let url = this.$location.url();
        schema['@id'] = url;

        let idenntifier = {};
        idenntifier['@type'] = 'PropertyValue';
        idenntifier['propertyID'] = 'local';
        let control = _get(item, 'pnx.control');
        idenntifier['value'] = this.getRecordId(control);

        schema['identifier'] = idenntifier;

        let name = title;
        schema['name'] = name;

        // create main entity
        let mainEntity = {};
        let type = _get(item, 'pnx.display.type[0]', 'book');
        let sotype = this.getRestToSDOType(type);
        mainEntity['@type'] = sotype;
        mainEntity['@id'] = '#' + sotype;

        // add creator
        try {
            let pnxau = _get(item, 'pnx.addata.addau');
            if (pnxau) {
                let creator = [];
                for (let auel of pnxau) {
                    let auelo = {};
                    auelo['@type'] = 'Person';
                    auelo['name'] = auel;
                    creator.push(auelo);
                }
                mainEntity['creator'] = creator;
            }
        } catch (e) {
            console.log(item);
            console.log(e);
        }

        // add author
        try {
            let pnxaucorp = _get(item, 'pnx.display.aucorp');
            if (pnxaucorp) {
                let contributor = [];
                for (let auel of pnxaucorp) {
                    let auelo = {};
                    auelo['@type'] = 'Person';
                    auelo['name'] = auel;
                    contributor.push(auelo);
                }
                mainEntity['contributor'] = contributor;
            }
        } catch (e) {
            console.log(item);
            console.log(e);
        }

        // add description
        try {
            let pnxdesc = _get(item, 'pnx.display.description');
            if (pnxdesc) {
                let description = [];
                for (let desc of pnxdesc) {
                    description.push(desc);
                }
                mainEntity['description'] = description;
            }
        } catch (e) {
            console.log(item);
            console.log(e);
        }

        // add edition
        try {
            let workExample = _get(item, 'pnx.display.edition[0]');
            if (workExample) {
                mainEntity['edition'] = workExample;
            }
        } catch (e) {
            console.log(item);
            console.log(e);
        }

        // Issn/eissn
        try {
            let pnxissn = _get(item, 'pnx.addata.issn');
            let issn = [];
            if (pnxissn) {
                for (let prissn of pnxissn) {
                    issn.push(prissn);
                }
            }
            pnxissn = _get(item, 'pnx.addata.eissn');
            if (pnxissn) {
                for (let prissn of pnxissn) {
                    issn.push(prissn);
                }
            }
            if (issn.length > 0) mainEntity['issn'] = issn;
        } catch (e) {
            console.log(item);
            console.log(e);
        }

        // indentifier

        let indentifier = {};
        indentifier['@type'] = 'PropertyValue';
        let indentifiers = [];

        let val = _get(item, 'pnx.display.sici');
        if (val) {
            let disval = {};
            disval['propertyID'] = 'SICI';
            disval['value'] = val;
            indentifiers.push(disval);
        }
        val = _get(item, 'pnx.display.coden');
        if (val) {
            let disval = {};
            disval['propertyID'] = 'CODEN';
            disval['value'] = val;
            indentifiers.push(disval);
        }
        val = _get(item, 'pnx.display.ean');
        if (val) {
            let disval = {};
            disval['propertyID'] = 'EAN';
            disval['value'] = val;
            indentifiers.push(disval);
        }
        val = _get(item, 'pnx.display.lccallnum');
        if (val) {
            let disval = {};
            disval['propertyID'] = 'LCCallNum';
            disval['value'] = val;
            indentifiers.push(disval);
        }
        val = _get(item, 'pnx.display.pmid');
        if (val) {
            let disval = {};
            disval['sameAs'] = 'https://www.ncbi.nlm.nih.gov/pubmed/' + val;
            indentifiers.push(disval);
        }
        val = _get(item, 'pnx.display.pmid');
        if (val) {
            let disval = {};
            disval['sameAs'] = 'https://lccn.loc.gov/' + val;
            indentifiers.push(disval);
        }

        if (indentifiers.length > 0) {
            indentifier['indentifier'] = indentifiers;
            mainEntity['indentifier'] = indentifier;
        }


        //sameAs
        /*
        let val = _get(item,'pnx.links.uri');
        let sameas = {};
        sameas['@type']='PropertyValue';
        let sameases = [];
        if(val) {
            let disval ={};
            disval['propertyID']='uri';
            disval['value']=val;
            disval['sameAs']='http://dx.doi.org/'+val;
            sameases.push(disval);
        }
        val = _get(item,'pnx.display.doi');
        if(val) {
            let disval ={};
            disval['propertyID']='doi';
            disval['value']=val;
            disval['sameAs']='http://dx.doi.org/'+val;
            sameases.push(disval);
        }
        if(sameases.length>0){
            sameas['sameas']=sameases;
            mainEntity['sameas']=sameas;
        }
        */


        // inLanguage
        try {
            let inLanguage = _get(item, 'pnx.display.language[0]');
            if (inLanguage) {
                mainEntity['inLanguage'] = inLanguage;
            }
        } catch (e) {
            console.log(item);
            console.log(e);
        }

        // publisher
        val = _get(item, 'pnx.display.publisher');
        if (val) {
            let disval = {};
            disval['@type'] = 'Organization';
            disval['name'] = val;
            mainEntity['publisher'] = disval;
        }


        // creationdate
        val = _get(item, 'pnx.display.creationdate[0]');
        if (val) {

            mainEntity['publisher'] = val;
        }


        // about

        try {
            let pnxabout = _get(item, 'pnx.display.subject');
            if (pnxabout) {
                for (let auel of pnxabout) {
                    let subs = auel.split(";");
                    for (let sub of subs) {
                        this.addAbout(mainEntity, 'Thing', sub.trim());
                    }
                }
            }
        } catch (e) {
            console.log(item);
            console.log(e);
        }


        // url
        val = _get(item, 'pnx.addata.url[0]');
        if (val) {

            mainEntity['url'] = val;
        }


        switch (sotype) {
            case 'Book':
                val = _get(item, 'pnx.addata.isbn[0]');
                if (val) {

                    mainEntity['isbn'] = val;
                }
                val = _get(item, 'pnx.display.format[0]');
                if (val) {

                    mainEntity['bookFormat'] = val;
                }
                val = _get(item, 'pnx.display.relation[0]');
                if (val) {

                    mainEntity['isPartOf'] = val;
                }
                break;
            case 'Article':
            case 'Periodical':
            case 'Chapter':
                let isPartOf = {};
                isPartOf['@id'] = '#periodical';
                let t = ['PublicationVolume', 'Periodical'];
                isPartOf['@type'] = t;
                val = _get(item, 'pnx.addata.title[0]');
                if (val) {
                    isPartOf['name'] = val;
                } else {
                    val = _get(item, 'pnx.addata.btitle[0]');
                    if (val) isPartOf['name'] = val;

                }
                val = _get(item, 'pnx.addata.issn[0]');
                if (val) isPartOf['issn'] = val;
                val = _get(item, 'pnx.addata.volume[0]');
                if (val) isPartOf['PublicationVolume'] = val;
                val = _get(item, 'pnx.display.periodical[0]');
                if (val) isPartOf['PublicationTitle'] = val;

                mainEntity['isPartOf'] = isPartOf;
                val = _get(item, 'pnx.addata.volume[0]');
                if (val) mainEntity['volumeNumber'] = val;
                val = _get(item, 'pnx.display.duns[0]');
                if (val) mainEntity['duns'] = val;
                val = _get(item, 'pnx.display.publicationplace[0]');
                if (val) mainEntity['Place'] = val;
                val = _get(item, 'pnx.display.relation[0]');
                if (val) mainEntity['additionalType'] = val;
                val = _get(item, 'pnx.display.periodical[0]');
                if (val) mainEntity['PublicationTitle'] = val;
                val = _get(item, 'pnx.addata.startpage[0]');
                if (val) mainEntity['pageStart'] = val;
                val = _get(item, 'pnx.addata.endpage[0]');
                if (val) mainEntity['pageEnd'] = val;
                val = _get(item, 'pnx.addata.pages[0]');
                if (val) mainEntity['pagination'] = val;
                val = _get(item, 'pnx.addata.issue[0]');
                if (val) mainEntity['issueNumber'] = val;


                break;
            case 'Photograph':
                break;
            case 'CreativeWork':
                break;
            case 'Movie':
                break;
            default:
                console.log('Not defined type: ' + sotype);
        }

        let val = _get(item, 'pnx.links.uri');
        this.evaluteLinks(mainEntity, val);


        schema['mainEntity'] = mainEntity;
        // finish main entity'
        return schema;
    }


    public evaluteLinks(mainEntity, links) {
        if (links) {
            for (let uri of links) {
                let URI = this.parseUri(uri);
                let A = URI['A'];
                let U = URI['U'];
                if (A) {
                    let V = URI['V'];
                    switch (A) {
                        case 'issn':
                        case 'isbn':
                        case 'lccn':
                        case 'oclc_nr':
                            //sameAs

                            let sameases = mainEntity['sameAs'];
                            if (!sameases) {
                                sameases = [];
                            }

                            sameases = sameases.concat(U);

                            mainEntity['sameAs'] = sameases;

                            break;
                        case 'subject':
                            //sameAs


                            //this.addAbout(mainEntity, 'Thing', V, U);
                            this.updateSameAsOfAboutIfExists(mainEntity, V, U);


                            break;
                        case 'creatorcontrib':
                            //sameAs

                            this.updateSameAsOfCreatorContributorsIfExists(mainEntity, V, U);
                            break;
                        default:
                            console.log('Not defined type: ' + sotype);
                    }
                }
            }
        }

    }

    public isbriefResultsJournalCoverageEnabledInPrimo() {
        return this.mappingTablesCache.findByProperties(this.mappingTablesCache.tables.INSTITUTION_PROPERTIES,
            {target: 'true', source1: 'brief_results_journal_coverage'}).length > 0  // OTB false
    }

    private parseUri(uri) {
        let urii = uri.split("$$");
        let URI = {};
        if (urii.length > 3) {
            URI['A'] = urii[1].substr(1);
            URI['V'] = urii[2].substr(1);
            URI['U'] = urii.slice(3).map(e => e.substr(1));
        }
        return URI;
    }

    private addAbout(mainEntity, type, name, sameAs) {
        let about = mainEntity['about'];
        if (!about) {
            about = [];
        }
        let aboutItem = {};
        aboutItem['@type'] = type;
        aboutItem['name'] = name;
        if (sameAs) {
            aboutItem['sameAs'] = sameAs;
        }
        about.push(aboutItem);
        mainEntity['about'] = about;
    }

    private updateSameAsOfAboutIfExists(mainEntity, name, sameAs) {
        let abouts = mainEntity['about'];
        if (abouts) {
            for (let about of abouts) {
                if (about['name'] === name) {
                    about['sameAs'] = sameAs;
                    return;
                }

            }
        }
        this.addAbout(mainEntity, 'Thing', name, sameAs);
    }

    private updateSameAsOfCreatorContributorsIfExists(mainEntity, name, sameAs) {
        let creators = mainEntity['creator'];
        this.updateSameAsOfCreatorsIfExists(creators, name, sameAs);
        let creators = mainEntity['contributor'];
        this.updateSameAsOfCreatorsIfExists(creators, name, sameAs);
    }

    private updateSameAsOfCreatorsIfExists(creators, name, sameAs) {
        if (creators) {
            for (let creator of creators) {
                if (creator['name'] === name) {
                    creator['sameAs'] = sameAs;
                }
            }
        }
    }

    public updateSDO(schema) {
        try {
            //console.log(schema);
            let allscripts = document.getElementsByTagName("script");
            for (let alscript of allscripts) {
                //console.log (alscript ) ;
                //console.log (alscript.type ) ;

                if (alscript.type === 'application/ld+json') {

                    alscript.innerHTML = JSON.stringify(schema);
                    return;
                }
            }
            console.log(' NOT FOUND application/ld+json script ');
            /*
            let alscripts = allscripts.filter(s => s.type === 'application/ld+json');
            if(alscripts){
                let alscript = alscripts[0];
                alscript.innerHTML = schema;

            }else{
                console.log (' NOT FOUND application/ld+json script ' ) ;
            }
            */
        } catch (e) {
            console.log(e);
            console.log("Error application/ld+json script");
        }

    }

    public removeSEOData() {
        this.updatePageTitle();
        this.updateSDO({});
        this.updatePageDescriptionFullView("");
        this.resetImageOf();
        this.clearResetURL();
    }


    public getReferenceEntry(query) {

        let dataToBeSent = {
            query: query,
            pckey: getCurrentPCKey()
        };

        let deliveryServiceUrl = this.restBaseURLs.pnxBaseURL + '/delivery?'
            + this.$httpParamSerializer(this.cheetah.searchData);

        let refEntryUrl = this.restBaseURLs.referenceEntryUrl;
        if (this.configurationUtil.isSuprima()) {
            refEntryUrl = refEntryUrl + "?vid=" + this.vid;
        }
        return this.$http.post(refEntryUrl, dataToBeSent).then(
            (response: { data: PrimoResponse.ISearchResponse }) => {
                if (response.data) {
                    let doc: any[] = [response.data];
                    if (this.configurationUtil.isSuprima()) {
                        return doc;
                    } else {
                        return this.$http.post(deliveryServiceUrl, {
                            "records": [response.data]
                        })
                    }
                } else {
                    return null;
                }
            });
    }

    getFacetsRetry(response, searchObject, resultObject) {
        if (_get(response, 'data.facets', []).length === 0) {
            let maxWaitTime = getPrimoSplitFacetsMaxWait();
            let timeout = this.$timeout(() => {
            }, maxWaitTime);
            this.getFacets(searchObject, resultObject, timeout);
        }
    }

    getFacets(searchObject, resultObject, timeout) {
        this.cheetah.performFacetSearch(searchObject, resultObject.info).then((facetResponse: { data: PrimoResponse.ISearchResponse }) => {
            let result = this.searchResultParser.parseSearchResponse(facetResponse.data);
            if (_get(result, 'facetsList', []).length > 0) {
                this.$timeout.cancel(timeout); //facets retrieved. Therefore we can stop the clock
                this.facetService.setResult(result.facetsList);
            } else {
                let waitInternal = getPrimoVeSplitFacetsWaitInterval();
                let waitBetweenTriesTimeout = this.$timeout(() => {
                    this.getFacets(searchObject, resultObject, timeout); //wait some time and try to get facets again
                }, waitInternal);

                timeout.then(() => { //timeout limit reached
                    this.$timeout.cancel(waitBetweenTriesTimeout); //cancel any pending facet retrieval attempts
                }, (error) => {
                    // nothing to do but need to handle error, so we don't get unhandled rejection error in console
                });
            }
        }, (err) => {
            this.$timeout.cancel(timeout);
        });
    }

    get showMobileFacets(): boolean {
        return this._showMobileFacets;
    }

    set showMobileFacets(value: boolean) {
        this._showMobileFacets = value;
    }

    public fromEsploro() {
        return typeof this.vid != 'undefined' && this.vid && this.vid.includes("ResearchRepository");
    }


    public getCanonicalFullDisplayUrl() {
        if (window.location.href.indexOf('/fulldisplay') < 0) {
            return;
        }
        if (window.location.href.indexOf('docid=') < 0 || window.location.href.indexOf('vid=') < 0) { //possible of Canonical link
            return window.location.href.split('?')[0];
        }
        let docid: string = window.location.href.split('docid=')[1].split('&')[0];
        let vid: string = window.location.href.split('vid=')[1].split('&')[0];
        return window.location.origin + window.location.pathname + '/' + docid + '/' + vid;
    }

    public clearListOfVersion(curFacet) {
        if (curFacet.persistent) {
            this.facetService.removeStickyFacet(curFacet);
        }
        let facetStrings = [];
        if (curFacet.mergedFacets) {
            facetStrings = curFacet.mergedFacets.map((facet) => facet.name + ',' + facet.type + ',' + facet.value);
        }
        facetStrings.push(curFacet.name + ',' + curFacet.type + ',' + curFacet.value);
        this.facetService.facetSearch({facet: facetStrings, remove: true});
    }

    public openCloseMobileFacets() {
        this.showMobileFacets = !this.showMobileFacets;
        // To solve issue PRM-43366, PRM-45561
        let listOfelements = document.getElementsByClassName('hide-when-facets-open');
        Array.prototype.forEach.call(listOfelements, (elem) => {
            elem.style.zIndex = !this.showMobileFacets ? "0" : "";
        })
    }
    public isSuprima() {
        return this.configurationUtil.isSuprima();
    }

    public isHighlightTheRecordYouCameFromEnabled() {
        return !!this.getHighlightTheRecordYouCameFromConf();
    }

    public isHighlightTheRecordYouCameFromFeatureFlagActive() {
        return this.isSuprima() ?
            this.configurationUtil.getFeatureFlag('highlight_the_record_you_came_from') :
            true;
    }

    private getHighlightTheRecordYouCameFromConf() {
        return this.mappingTablesCache.findByProperties(this.mappingTablesCache.tables.VIEW_PROPERTIES,
            {source1: getVid(), source2: this.ENABLE_HIGHLIGHT_THE_RECORD_YOU_CAME_FROM, target: 'true'}).length > 0;
    }

    private updateStateParamsWithNewPcAvailabilityForNewsPapersSearch(newPcAvailability, previousPcAvailability) {
        if (this.isNewsPapersSearch() && (previousPcAvailability !== newPcAvailability)) {
            newPcAvailability = (typeof newPcAvailability === 'boolean') ? String(newPcAvailability) : newPcAvailability;
            this.$stateParams['pcAvailability'] = newPcAvailability;
        }
    }

    private isNewsPapersSearch() {
        return this.$state.current.name === STATE_NAMES.NEWSPAPERS;
    }

    private getIndexTypeLabel(code) {
        code = this.getFirstStringValue(code);

        if (!this.isSafeTranslationSuffix(code)) {
            console.warn('[XSS protection] Unsafe translation key suffix blocked in getIndexTypeLabel:', code);
            return '';
        }

        let key = 'search-advanced.scope.option.nui.advanced.index.' + code;
        let translated = this.$translate.instant(key);

        return translated === key ? code : translated;
    }

    private getFirstStringValue(value) {
        if (angular.isArray(value)) {
            value = value[0];
        }

        return typeof value === 'string' ? value : '';
    }

    private isSafeTranslationSuffix(value: string): boolean {
        return /^[a-zA-Z0-9_.-]+$/.test(value) ||
            /^\{\d+\}$/.test(value);
    }



    private getScopeLabel(code: string) {
        return this.$translate.instant('tabbedmenu.' + code + '.label')
    }

    private getOperatorType(searchType: any) {
        let operator = searchType;
        if(angular.isArray(searchType) && searchType.length == 1){
            operator = searchType[0];
        }
        switch (operator) {
            case 'contains':
                return 'Contains';
            case 'exact':
                return 'Contains Exact Phrase';
            case 'begins_with':
                return 'Starts With';
            case 'equals':
                return 'Equals Exact Phrase';
            default: return searchType;
        }
    }

    public async invokeDummy(delayms: number): Promise<any> {
        const dummyApiUrl = this.restBaseURLs.dummyApiUrl;
        const urlWithDelay = `${dummyApiUrl}?vid=${this.vid}&delayms=${delayms}`;
        try {
            const response = await this.$http.get(urlWithDelay);
            console.log('API response:', response.data);
            return response.data;
        } catch (error) {
            console.error('API error:', error);
            throw error;
        }
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/components/search/search.service.ts